import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PinKeypad from "./PinKeypad";
import { ShieldAlert, Lock, KeyRound } from "lucide-react";
import { operationLabel } from "./operationLabels";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable PIN verification dialog.
 *
 * Usage: rendered globally by PinProvider. Not intended to be mounted
 * directly by feature components — they call `pin.require(operation)`.
 */
export default function PinDialog({
  open,
  operation,
  onClose,
  onSuccess,
  onRequestRecovery,
}) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(null); // { remaining_seconds }

  useEffect(() => {
    if (!open) return;
    setPin("");
    setError("");
    setBusy(false);
    setLocked(null);
    // Refresh lockout state on open
    (async () => {
      try {
        const { data } = await api.get("/security/status");
        if (data.locked) setLocked({ remaining: data.locked_seconds_remaining });
      } catch (_) { /* silently ignore — dialog still usable */ }
    })();
  }, [open]);

  // Countdown when locked
  useEffect(() => {
    if (!locked || !locked.remaining) return;
    const id = setInterval(() => {
      setLocked((prev) => {
        if (!prev) return prev;
        const next = Math.max(0, prev.remaining - 1);
        if (next === 0) return null;
        return { remaining: next };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [locked]);

  const verify = async (fullPin) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/security/pin/verify", {
        pin: fullPin,
        operation: operation || "",
      });
      onSuccess?.(data.sudo_token, data.expires_at);
    } catch (err) {
      const status = err?.response?.status;
      const detail = formatApiError(err?.response?.data?.detail) || "Error";
      setError(detail);
      setPin("");
      if (status === 423) {
        // locked — reload status to get exact remaining time
        try {
          const { data } = await api.get("/security/status");
          if (data.locked) setLocked({ remaining: data.locked_seconds_remaining });
        } catch (_) { /* ignore */ }
      }
    } finally {
      setBusy(false);
    }
  };

  const isLocked = locked && locked.remaining > 0;
  const opText = operation ? operationLabel(operation) : "una acción sensible";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="pin-dialog">
        <div className="bg-gradient-to-br from-orange-50 to-white px-6 pt-6 pb-4 border-b border-orange-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-white shadow-sm">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <DialogHeader className="p-0 space-y-0">
                <DialogTitle className="text-base font-bold text-neutral-900">PIN maestro</DialogTitle>
                <DialogDescription className="text-xs text-neutral-600">
                  Introduce tu PIN para autorizar: <span className="font-semibold text-neutral-800">{opText}</span>
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          {isLocked ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-2 text-center" data-testid="pin-locked-banner">
              <div className="flex justify-center">
                <Lock className="h-6 w-6 text-red-500" />
              </div>
              <div className="text-sm font-bold text-red-800">PIN bloqueado temporalmente</div>
              <div className="text-xs text-red-700">
                Se han excedido los intentos permitidos. Espera{" "}
                <span className="font-semibold tabular-nums">
                  {Math.floor(locked.remaining / 60)}m {String(locked.remaining % 60).padStart(2, "0")}s
                </span>{" "}
                o usa la opción &quot;Recuperar PIN&quot;.
              </div>
            </div>
          ) : (
            <PinKeypad
              value={pin}
              onChange={setPin}
              onSubmit={verify}
              disabled={busy}
              testIdPrefix="pin-verify"
            />
          )}

          {error && !isLocked && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-start gap-2" data-testid="pin-error">
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mt-5 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => { onClose?.(); }}
              className="text-neutral-500 hover:text-neutral-800"
              data-testid="pin-cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => { onRequestRecovery?.(); }}
              className="text-orange-600 hover:text-orange-800 font-medium"
              data-testid="pin-open-recovery"
            >
              ¿Olvidaste tu PIN?
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
